import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { BookMarked, Download, Sparkles, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { practiceComplianceAPI as API, MANUAL_STATUS_META, downloadManualPdf } from '../../lib/practiceComplianceAPI';

export const PMManualsTab = ({ onChanged }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const res = await API.listManuals(); setData(res.data); } catch { /* */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const res = await API.generate();
      toast.success(`${res.data.generated.length} manual(s) generated as drafts — pending principal approval`);
      await load();
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Generation failed'); }
    finally { setBusy(false); }
  };

  const generateOne = async (key) => {
    setBusy(true);
    try {
      await API.generate([key]);
      toast.success('Draft generated');
      await load();
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Generation failed'); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="animate-pulse h-48 bg-muted rounded-lg mt-4" />;
  const byKey = Object.fromEntries((data.manuals || []).map((m) => [m.template_key, m]));

  return (
    <div className="space-y-4 mt-4">
      {data.can_manage && (
        <div className="flex justify-end">
          <Button onClick={generate} disabled={busy} data-testid="pm-generate-all">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Generate / update drafts from profile
          </Button>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {data.templates.map((t) => {
          const m = byKey[t.key];
          const eff = m?.effective_version;
          const working = m?.working_version;
          return (
            <Card key={t.key} className="flex flex-col" data-testid={`pm-manual-card-${t.key}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2"><BookMarked className="w-4 h-4 text-primary" /> {t.code}</CardTitle>
                <CardDescription className="text-xs">{t.subtitle}</CardDescription>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col justify-between space-y-3">
                <div className="space-y-2">
                  {t.conditional && (
                    <div className="flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> Conditional — only applies if the practice performs in-scope assurance engagements (enable in Practice Profile).
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    {eff && <Badge className={MANUAL_STATUS_META.approved_effective.color}>Effective v{eff.version}</Badge>}
                    {working && <Badge className={(MANUAL_STATUS_META[working.status] || {}).color}>{(MANUAL_STATUS_META[working.status] || {}).label} v{working.version}</Badge>}
                    {!m && <Badge variant="outline">Not generated</Badge>}
                  </div>
                  {m && eff && (
                    <p className="text-xs text-muted-foreground">
                      {m.acknowledgement_count} acknowledgement(s){m.my_acknowledged ? ' · you acknowledged ✓' : ''}
                      {m.next_review_date ? ` · review ${m.next_review_date}` : ''}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  {m ? (
                    <>
                      <Button size="sm" className="flex-1" onClick={() => navigate(`/practice-manuals/${m.id}`)} data-testid={`pm-open-${t.key}`}>Open</Button>
                      <Button size="sm" variant="outline" data-testid={`pm-pdf-${t.key}`}
                        onClick={() => downloadManualPdf(m.id, null, `${t.code.replace(' ', '_')}`).catch(() => toast.error('PDF failed'))}>
                        <Download className="w-4 h-4" />
                      </Button>
                    </>
                  ) : data.can_manage ? (
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => generateOne(t.key)} disabled={busy} data-testid={`pm-generate-${t.key}`}>
                      Generate draft
                    </Button>
                  ) : (
                    <p className="text-xs text-muted-foreground">Awaiting generation by the principal.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground">
        Manuals are generated from your practice profile as drafts and are never represented as certified or implemented until the principal approves them.
      </p>
    </div>
  );
};
